import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authenticatedFetch } from '../../lib/api';
import {
  MdChat,
  MdSend,
  MdAttachFile,
  MdClose,
  MdArrowForward,
  MdCloudUpload,
  MdNotificationsActive,
  MdOutlineNotificationsOff,
} from 'react-icons/md';
import { ChatSessionStatus, ChatMessageType } from '@euroasiann/shared';

interface ChatMessage {
  _id: string;
  sessionId: string;
  senderId?: string;
  senderType: 'user' | 'agent' | 'system';
  senderName: string;
  messageType: ChatMessageType;
  content: string;
  fileUrl?: string;
  fileName?: string;
  createdAt: string;
}

interface ChatSession {
  _id: string;
  portalType: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  organizationId?: {
    _id: string;
    name: string;
  };
  agentId?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  status: ChatSessionStatus;
  lastMessageAt: string;
  createdAt: string;
}

interface OnlineAgent {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  portalType: string;
}

export default function SupportChatPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [onlineAgents, setOnlineAgents] = useState<OnlineAgent[]>([]);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const ringingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 1️⃣ Fetch Active Chat Sessions and Agents
  const fetchSessions = async () => {
    try {
      const res = await authenticatedFetch('/api/v1/chat/sessions');
      if (res.ok) {
        const json = await res.json();
        if (json.success) setSessions(json.data);
      }
    } catch (err) {
      console.error('Error fetching chat sessions:', err);
    }
  };

  const fetchOnlineAgents = async () => {
    try {
      const res = await authenticatedFetch('/api/v1/chat/agents/online');
      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          // Exclude current agent from list
          const list = json.data.filter((a: OnlineAgent) => a._id !== user?.userId);
          setOnlineAgents(list);
        }
      }
    } catch (err) {
      console.error('Error fetching online agents:', err);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchOnlineAgents();
  }, [user]);

  // 2️⃣ Ringing Sound Synthesizer (Telephone Ring)
  const startRinging = () => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      const playRingTone = () => {
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(440, ctx.currentTime); // A4
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(480, ctx.currentTime);

        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime + 1.2);
        gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.4);

        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(ctx.destination);

        osc1.start();
        osc2.start();

        osc1.stop(ctx.currentTime + 1.5);
        osc2.stop(ctx.currentTime + 1.5);
      };

      playRingTone();
      ringingIntervalRef.current = setInterval(playRingTone, 3000);
    } catch (err) {
      console.warn('AudioContext ringing failed to play:', err);
    }
  };

  const stopRinging = () => {
    if (ringingIntervalRef.current) {
      clearInterval(ringingIntervalRef.current);
      ringingIntervalRef.current = null;
    }
  };

  // 3️⃣ WebSocket Connection
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    // Use location.host for dynamic URL, fall back to localhost:3000
    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProto}//localhost:3000/ws/chat?token=${token}`;

    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log('Support Chat WebSocket connected');
    };

    ws.onmessage = (event) => {
      const { event: wsEvent, data } = JSON.parse(event.data);
      if (!wsEvent) return;

      switch (wsEvent) {
        case 'chat:assigned':
          // Re-fetch sessions to see newly assigned chat
          fetchSessions();
          // Trigger ringing sound and notification popup
          if (data.agentId === user?.userId) {
            startRinging();
            // Show custom HTML notification
            showNotificationToast(data);
          }
          break;
        case 'chat:waiting_alert':
          fetchSessions();
          break;
        case 'chat:message':
          // Add to current chat thread if active
          if (activeSession && data.sessionId === activeSession._id) {
            setMessages((prev) => [...prev, data]);
          }
          // Update sessions list to show last message
          fetchSessions();
          break;
        case 'chat:closed':
          if (activeSession && data.sessionId === activeSession._id) {
            setActiveSession((prev) => prev ? { ...prev, status: ChatSessionStatus.CLOSED } : null);
            // Append system closed message
            const closedSystemMsg: ChatMessage = {
              _id: `system-${Date.now()}`,
              sessionId: data.sessionId,
              senderType: 'system',
              senderName: 'System',
              messageType: ChatMessageType.TEXT,
              content: 'This chat session has been closed.',
              createdAt: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, closedSystemMsg]);
          }
          fetchSessions();
          break;
      }
    };

    ws.onclose = () => {
      console.log('Support Chat WebSocket disconnected');
    };

    return () => {
      ws.close();
      stopRinging();
    };
  }, [activeSession, user, soundEnabled]);

  // 4️⃣ Scroll Messages Thread
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 5️⃣ Load Messages for Selected Session
  const handleSelectSession = async (session: ChatSession) => {
    setActiveSession(session);
    stopRinging(); // Stop ringing sound on acceptance/click
    try {
      const res = await authenticatedFetch(`/api/v1/chat/messages/${session._id}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success) setMessages(json.data);
      }
    } catch (err) {
      console.error('Error fetching chat messages:', err);
    }
  };

  // 6️⃣ Send Message
  const handleSendMessage = () => {
    if (!inputText.trim() || !activeSession || !socketRef.current) return;

    socketRef.current.send(
      JSON.stringify({
        event: 'chat:message',
        data: {
          sessionId: activeSession._id,
          content: inputText,
          messageType: ChatMessageType.TEXT,
        },
      })
    );
    setInputText('');
  };

  // 7️⃣ File / Image Attachment Upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeSession || !socketRef.current) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Get auth headers
      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/v1/chat/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          const { fileUrl, fileName, fileType } = json.data;
          // Send over WebSocket
          socketRef.current.send(
            JSON.stringify({
              event: 'chat:message',
              data: {
                sessionId: activeSession._id,
                content: `Shared file: ${fileName}`,
                messageType: fileType,
                fileUrl,
                fileName,
              },
            })
          );
        }
      }
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // 8️⃣ Transfer Chat
  const handleTransferChat = (targetAgentId: string) => {
    if (!activeSession || !socketRef.current) return;

    socketRef.current.send(
      JSON.stringify({
        event: 'chat:transfer',
        data: {
          sessionId: activeSession._id,
          targetAgentId,
        },
      })
    );
    setShowTransferModal(false);
    setActiveSession(null);
    setMessages([]);
    fetchSessions();
  };

  // 9️⃣ Close Chat
  const handleCloseChat = () => {
    if (!activeSession || !socketRef.current) return;

    socketRef.current.send(
      JSON.stringify({
        event: 'chat:close',
        data: {
          sessionId: activeSession._id,
        },
      })
    );
  };

  // Notification Toast pop-up
  const [notification, setNotification] = useState<{ id: string; userName: string; portalType: string } | null>(null);

  const showNotificationToast = (data: any) => {
    setNotification({
      id: data.sessionId,
      userName: data.userName || 'New Client',
      portalType: data.portalType || 'customer',
    });
    // Autohide after 10 seconds
    setTimeout(() => {
      setNotification(null);
    }, 10000);
  };

  return (
    <div className="flex h-[calc(100vh-100px)] w-full gap-4 overflow-hidden rounded-2xl bg-slate-900/30 p-2 text-slate-100 backdrop-blur-xl">
      {/* 📥 CUSTOM SLIDE-IN POPUP TOAST */}
      {notification && (
        <div 
          onClick={() => {
            const found = sessions.find((s) => s._id === notification.id);
            if (found) handleSelectSession(found);
            setNotification(null);
          }}
          className="fixed bottom-6 right-6 z-50 flex cursor-pointer items-center gap-4 rounded-xl border border-blue-500/30 bg-slate-950/90 p-4 shadow-2xl animate-bounce backdrop-blur"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white font-bold">
            <MdChat className="h-5 w-5" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-white">Support Chat Assigned</h4>
            <p className="text-xs text-[#0088cc]/45">
              {notification.userName} ({notification.portalType}) is waiting. Click to join.
            </p>
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setNotification(null);
            }}
            className="rounded p-1 hover:bg-slate-800 text-[#0088cc]/65 hover:text-white"
          >
            <MdClose className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* 📇 SESSIONS PANEL (LEFT) */}
      <div className="flex w-80 flex-col rounded-xl border border-slate-800 bg-slate-950/80 p-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <MdChat className="text-blue-500" /> Live Support
          </h2>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="rounded-lg p-1.5 hover:bg-slate-800 text-[#0088cc]/65 hover:text-white"
            title={soundEnabled ? 'Mute sound alerts' : 'Enable sound alerts'}
          >
            {soundEnabled ? <MdNotificationsActive className="h-5 w-5 text-blue-500" /> : <MdOutlineNotificationsOff className="h-5 w-5" />}
          </button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto pr-1">
          {sessions.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center text-[hsl(var(--muted-foreground))] text-sm">
              <p>No active chat sessions.</p>
              <p className="text-xs mt-1">Waiting for users to initiate...</p>
            </div>
          ) : (
            sessions.map((session) => {
              const isSelected = activeSession && activeSession._id === session._id;
              const isWaiting = session.status === ChatSessionStatus.WAITING;

              return (
                <div
                  key={session._id}
                  onClick={() => handleSelectSession(session)}
                  className={`flex cursor-pointer items-center justify-between rounded-lg p-3 transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : isWaiting
                      ? 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/20'
                      : 'bg-slate-900/60 text-[#0088cc]/45 hover:bg-slate-800'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                      {session.userId ? `${session.userId.firstName} ${session.userId.lastName}` : 'Client'}
                    </p>
                    <p className="truncate text-xs opacity-80">
                      {session.portalType.toUpperCase()} • {session.organizationId?.name || 'Freelancer'}
                    </p>
                  </div>
                  {isWaiting && (
                    <span className="flex h-2 w-2 rounded-full bg-yellow-400 animate-pulse ml-2" />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 💬 ACTIVE CHAT PANEL (RIGHT) */}
      <div className="flex flex-1 flex-col rounded-xl border border-slate-800 bg-slate-950/80">
        {activeSession ? (
          <>
            {/* Thread Header */}
            <div className="flex items-center justify-between border-b border-slate-800 p-4 bg-slate-900/20">
              <div>
                <h3 className="text-sm font-bold text-white">
                  {activeSession.userId ? `${activeSession.userId.firstName} ${activeSession.userId.lastName}` : 'Support Client'}
                </h3>
                <p className="text-xs text-[#0088cc]/65">
                  {activeSession.portalType.toUpperCase()} Portal • {activeSession.organizationId?.name || 'Guest'}
                </p>
              </div>
              
              <div className="flex gap-2">
                {activeSession.status !== ChatSessionStatus.CLOSED && (
                  <>
                    <button
                      onClick={() => setShowTransferModal(true)}
                      className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 px-3 py-1.5 text-xs text-[#0088cc]/45 hover:bg-slate-800"
                    >
                      <MdArrowForward className="h-4 w-4" /> Transfer
                    </button>
                    <button
                      onClick={handleCloseChat}
                      className="flex items-center gap-1 rounded-lg bg-red-600/20 border border-red-500/30 px-3 py-1.5 text-xs text-red-400 hover:bg-red-600/30"
                    >
                      <MdClose className="h-4 w-4" /> Close Chat
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => {
                const isAgentMsg = msg.senderType === 'agent';
                const isSystem = msg.senderType === 'system';

                if (isSystem) {
                  return (
                    <div key={msg._id} className="flex justify-center">
                      <span className="rounded bg-slate-900 px-2.5 py-1 text-xs text-[#0088cc]/65 italic">
                        {msg.content}
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    key={msg._id}
                    className={`flex ${isAgentMsg ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md rounded-xl p-3 shadow-md ${
                        isAgentMsg
                          ? 'bg-blue-600 text-white rounded-tr-none'
                          : 'bg-slate-900 text-slate-200 rounded-tl-none border border-slate-800'
                      }`}
                    >
                      <p className="text-[10px] opacity-70 mb-1 font-bold">
                        {msg.senderName}
                      </p>
                      
                      {msg.messageType === ChatMessageType.TEXT ? (
                        <p className="text-sm break-words">{msg.content}</p>
                      ) : msg.messageType === ChatMessageType.IMAGE ? (
                        <div className="space-y-1">
                          <img
                            src={msg.fileUrl}
                            alt="Attachment"
                            className="max-h-60 rounded-lg object-contain cursor-zoom-in"
                            onClick={() => window.open(msg.fileUrl, '_blank')}
                          />
                          <p className="text-xs truncate">{msg.fileName}</p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 rounded bg-slate-950/40 p-2">
                          <MdAttachFile className="h-5 w-5 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold">{msg.fileName}</p>
                            <a
                              href={msg.fileUrl}
                              download={msg.fileName}
                              className="text-xs text-blue-400 hover:underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Download
                            </a>
                          </div>
                        </div>
                      )}
                      
                      <span className="block text-[8px] opacity-60 text-right mt-1.5">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Thread Footer Input */}
            {activeSession.status !== ChatSessionStatus.CLOSED ? (
              <div className="border-t border-slate-800 p-4 flex items-center gap-2 bg-slate-900/10">
                {/* File Upload Button */}
                <label className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-slate-800 bg-slate-900 text-[#0088cc]/65 hover:bg-slate-800 hover:text-white transition-colors">
                  <MdAttachFile className="h-5 w-5" />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                  />
                </label>

                {/* Message Input */}
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={isUploading ? 'Uploading file...' : 'Type a reply...'}
                  disabled={isUploading}
                  className="flex-1 rounded-lg border border-slate-800 bg-slate-950 px-4 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                />

                {/* Send Button */}
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isUploading}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <MdSend className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="p-4 bg-slate-950 text-center text-xs text-[hsl(var(--muted-foreground))] italic border-t border-slate-800">
                This chat session is closed. You can review the chat history above.
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center text-[hsl(var(--muted-foreground))] p-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-900 text-[#0088cc]/65">
              <MdChat className="h-8 w-8" />
            </div>
            <h3 className="text-base font-bold text-white">No Chat Selected</h3>
            <p className="text-sm max-w-sm mt-1">
              Select an active session from the left panel to join the support chat.
            </p>
          </div>
        )}
      </div>

      {/* 🤝 CHAT TRANSFER MODAL */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-96 rounded-xl border border-slate-800 bg-slate-950 p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Transfer Support Chat</h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="rounded p-1 hover:bg-slate-900 text-[#0088cc]/65 hover:text-white"
              >
                <MdClose className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-xs text-[#0088cc]/65 mb-4">
              Select another online agent from Tech or Admin portal to transfer this active support chat.
            </p>

            <div className="max-h-60 overflow-y-auto space-y-2 mb-4">
              {onlineAgents.length === 0 ? (
                <div className="p-4 text-center text-xs text-[hsl(var(--muted-foreground))] italic border border-slate-900 rounded-lg">
                  No other online agents found.
                </div>
              ) : (
                onlineAgents.map((agent) => (
                  <div
                    key={agent._id}
                    onClick={() => handleTransferChat(agent._id)}
                    className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-900 bg-slate-900/40 p-3 hover:bg-slate-800/80"
                  >
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {agent.firstName} {agent.lastName}
                      </p>
                      <p className="text-[10px] text-[#0088cc]/65">
                        {agent.portalType.toUpperCase()} • {agent.role}
                      </p>
                    </div>
                    <MdArrowForward className="h-4 w-4 text-blue-500" />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
